import type { Context } from 'chartjs-plugin-datalabels'
import type { ChartConfiguration } from '@/stores/type/chartConfiguration.type'
import type { DatasetType } from '@/views/calculations/type/resultView.type'
import { CHART_DISPLAY_MODES } from '@/constants/chartConfiguration'
import { formatCompactMoney, formatMoney } from '@/views/calculations/utils/resultChart/format'
import { getStackBreakdown } from '@/views/calculations/utils/resultChart/metrics'

const isTotal = (datasets: DatasetType[], index: number) => datasets[index]?.isTotal === true

export const buildResultChartScales = (
  config: Pick<ChartConfiguration, 'x_axis_title' | 'y_axis_title'>
) => ({
  x: {
    stacked: true,
    title: {
      display: true,
      text: config.x_axis_title || 'Location',
      font: { weight: 'bold' as const }
    }
  },
  y: {
    stacked: true,
    beginAtZero: true,
    title: {
      display: true,
      text: config.y_axis_title || 'MKI [euro]',
      font: { weight: 'bold' as const }
    },
    ticks: { callback: (value: number | string) => formatMoney(Number(value)) }
  }
})

type DatalabelParams = {
  getDatasets: () => DatasetType[]
  displayMode: () => string
  threshold?: () => number
}

const OUTSIDE_LABEL_COLOR = '#000000'
const INSIDE_LABEL_COLOR = '#ffffff'
const LABEL_ROW_GAP = 12
const TIP_CLEARANCE = 4
// Bold 8px digits measure ~4.6px each — an estimate, and `display: 'auto'` still backs it up.
const COMPACT_CHAR_WIDTH = 4.6

type ColumnLabel = { index: number; height: number; x: number; width: number; offset: number }

export const buildResultChartDataLabels = ({
  getDatasets,
  displayMode,
  threshold
}: DatalabelParams) => {
  const isGrouped = () => displayMode() === CHART_DISPLAY_MODES.PHASE_GROUPED
  const isOutside = (ctx: Context) => isTotal(getDatasets(), ctx.datasetIndex) || isGrouped()
  const valueAt = (ctx: Context) =>
    Number(getDatasets()[ctx.datasetIndex]?.data[ctx.dataIndex] ?? 0)
  // For a vertical bar 'end' is always the rect's upper edge and 'start' its lower one, so a
  // positive bar's tip is 'end' and its foot at the axis is 'start' — reversed for a negative bar.
  // Read as an `align` the same two values simply mean up and down, which is why one helper drives
  // both.
  const outsideEdge = (ctx: Context): 'start' | 'end' => (valueAt(ctx) < 0 ? 'start' : 'end')
  const axisEdge = (ctx: Context): 'start' | 'end' => (valueAt(ctx) < 0 ? 'end' : 'start')

  // Labels in the near-axis band all land in the same 10px strip, so a column is walked outward
  // from its shortest bar. A label only has to clear the ones it actually overlaps horizontally;
  // the rest stay on their own tip. Distances are from the axis, which is what `axisEdge` anchors
  // to.
  const cascadeOffset = (ctx: Context) => {
    const scale = ctx.chart.scales.y
    const axisPixel = scale.getPixelForValue(0)
    const negative = valueAt(ctx) < 0
    const datasets = getDatasets()
    const bars: ColumnLabel[] = []

    for (let index = 0; index < datasets.length; index += 1) {
      const value = Number(datasets[index].data[ctx.dataIndex] ?? 0)
      if (negative ? value < 0 : value > 0) {
        bars.push({
          index,
          height: Math.abs(scale.getPixelForValue(value) - axisPixel),
          x: ctx.chart.getDatasetMeta(index).data[ctx.dataIndex]?.x ?? 0,
          width: formatCompactMoney(value).length * COMPACT_CHAR_WIDTH,
          offset: 0
        })
      }
    }
    bars.sort((first, second) => first.height - second.height)

    for (let outer = 0; outer < bars.length; outer += 1) {
      const bar = bars[outer]
      bar.offset = bar.height + TIP_CLEARANCE
      for (let inner = 0; inner < outer; inner += 1) {
        const placed = bars[inner]
        if (Math.abs(placed.x - bar.x) < (placed.width + bar.width) / 2) {
          bar.offset = Math.max(bar.offset, placed.offset + LABEL_ROW_GAP)
        }
      }
      if (bar.index === ctx.datasetIndex) return bar.offset
    }

    return TIP_CLEARANCE
  }

  return {
    font: () => ({ weight: 'bold' as const, size: isGrouped() ? 8 : 10 }),
    clamp: true,
    padding: () => (isGrouped() ? 0 : 4),
    display: (ctx: Context) => (isTotal(getDatasets(), ctx.datasetIndex) ? true : 'auto'),
    color: (ctx: Context) => (isOutside(ctx) ? OUTSIDE_LABEL_COLOR : INSIDE_LABEL_COLOR),
    // Separated bars measure from the axis so the cascade can stack a column; everywhere else the
    // label still hangs off the bar's own tip.
    anchor: (ctx: Context) => {
      if (isGrouped()) return axisEdge(ctx)
      return isOutside(ctx) ? outsideEdge(ctx) : 'center'
    },
    align: (ctx: Context) => (isOutside(ctx) ? outsideEdge(ctx) : 'center'),
    offset: (ctx: Context) => (isGrouped() ? cascadeOffset(ctx) : 4),
    formatter: (value: number, ctx: Context) => {
      const datasets = getDatasets()
      const dataset = datasets[ctx.datasetIndex]
      const numericValue = Number(value ?? 0)

      if (displayMode() === CHART_DISPLAY_MODES.PHASE_GROUPED) {
        return numericValue === 0 ? '' : formatCompactMoney(numericValue)
      }
      if (dataset?.isTotal) {
        return formatMoney(getStackBreakdown(datasets, ctx.dataIndex).net)
      }
      if (threshold) {
        const max = threshold()
        if (max === 0) return ''
        return Math.abs(numericValue) / max > 0.05 ? formatMoney(numericValue) : ''
      }
      return numericValue === 0 ? '' : formatMoney(numericValue)
    }
  }
}
